/**
 * Familienkonto-Adapter (CRM 2.0, v2 admin-scoped)
 *
 * Ein Familienkonto bündelt mehrere Personen (Profile). Jede Person hat genau
 * EINE Rolle (Profiltyp), einen Pflegegrad (kein/1–5), einen Familiengrad und
 * die Info „lebt im Haushalt". Frau/Mann-Rollen sind Pflegeempfänger
 * (is_care_recipient), alle anderen (Rechnungsempfänger/Hausarzt/Kontakt) sind
 * schlanke Kontakt-Profile.
 *
 * Backend-Vertrag (alles im `{ data, meta, errors }`-Envelope der v2-API):
 *   GET    /v2/admin/family-accounts?search=&assigned_to=&page=&per_page=
 *   GET    /v2/admin/family-accounts/{id}
 *   POST   /v2/admin/family-accounts
 *   PUT    /v2/admin/family-accounts/{id}
 *   POST   /v2/admin/family-accounts/{id}/members
 *   GET    /v2/admin/profiles/{id}
 *   PUT    /v2/admin/profiles/{id}
 *   DELETE /v2/admin/profiles/{id}
 *
 * Konventionen wie in `v1.ts`: getippte Wrapper geben die Entität direkt
 * zurück (das `data`/`meta`-Auspacken passiert hier), Fehler (422/403/401)
 * werden als Axios-Fehler durchgereicht — die aufrufende Seite zeigt die
 * Message aus `err.response.data.errors`.
 */

import { apiV2 } from './api';
import { rethrowStaleWrite } from './optimisticLock';

/* ─── Pagination-Meta (v2-Envelope) ─────────────────────────────────────── */

export interface V2Meta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface V2Paginated<T> {
  data: T[];
  meta: V2Meta;
}

/* ─── Domänen-Typen ─────────────────────────────────────────────────────── */

/** Adresse eines Familienmitglieds. */
export interface FamilyMemberAddress {
  strasse: string | null;
  hausnummer: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
}

/**
 * Eine Person innerhalb eines Familienkontos.
 *
 * `is_care_recipient` ist true, sobald der Profiltyp Frau/Mann ist — dann
 * werden im Formular die vollen Pflege-Sektionen gezeigt, sonst nur die
 * schlanke Kontakt-Sicht.
 */
export interface FamilyMember {
  user_id: number | null;
  profile_id: number;
  /** Rolle / Profiltyp (String-Wert bzw. Options-ID aus der profiltyp-Gruppe). */
  profiltyp: string | null;
  /** Feedback 11.10.2026 — alle Rollen (primäre zuerst). */
  profiltypen?: string[];
  /** Familiengrad (Array von Werten aus der familiengrad-Gruppe — Multi-Select). */
  familiengrad: string[];
  /** Lebt die Person im Haushalt des Pflegebedürftigen? */
  haushalt: boolean;
  /** Pflegegrad 1–5 oder null („kein"). */
  pflegegrad: number | null;
  /** true bei Profiltyp Frau/Mann → volle Pflege-Sektionen. */
  is_care_recipient: boolean;
  /**
   * Expliziter Pflegeempfänger-Toggle — unabhängig vom Profiltyp. Wenn true,
   * werden die vollen Pflege-Sektionen gezeigt und alle Tabs in PbDetailPage.
   */
  is_pflegeempfaenger: boolean;
  anrede: string | null;
  titel: string | null;
  vorname: string | null;
  nachname: string | null;
  fullname: string | null;
  email: string | null;
  mobile: string | null;
  telefon: string | null;
  address: FamilyMemberAddress;
  /** Phase des Pflege-Prozesses (nur bei Pflegeempfängern relevant). */
  phase: number | null;
  /** SGB V (Behandlungspflege) aus den Profildaten: 'benoetigt' | 'nicht_benoetigt' | null. */
  sgb5?: string | null;
  /**
   * Ist die Person Kunde (erscheint unter /pflege)? Backend-Definition
   * User::scopeKunde: letzte aktive Stage + bestätigte Care. Nur in der
   * Konto-Detailantwort gesetzt; bis dahin blendet die Detailseite den
   * Prozessbalken ein und markiert „Anfragen" im Menü.
   */
  is_kunde?: boolean | null;
  /**
   * Optimistisches Locking: jüngster Stand aus Profil + User (ISO-8601).
   * Wird beim Speichern als `updated_at` zurückgeschickt; siehe
   * src/lib/optimisticLock.ts.
   */
  updated_at?: string | null;
}

/** Zusammenfassung eines Familienkontos (Liste + Detail). */
/** Abwesenheit eines Kontomitglieds im laufenden Monat (Listenspalte). */
export interface FamilyAccountAbsence {
  grund: string | null;
  startdatum: string | null;
  enddatum: string | null;
  /** true = läuft gerade; false = diesen Monat beendet oder bevorstehend. */
  running: boolean;
  /** Name der abwesenden Person im Konto (oder null). */
  member: string | null;
}

/** Serverseitige Vollständigkeit (FamilyAccountCompleteness). */
export interface FamilyAccountCompleteness {
  complete: boolean;
  /** Feldnamen der fehlenden Pflichtfelder. */
  missing: string[];
}

export interface FamilyAccount extends WohnenFields {
  id: number;
  /** Fehlt bei Antworten älterer API-Stände. */
  completeness?: FamilyAccountCompleteness;
  name: string | null;
  assigned_to: number | null;
  notiz: string | null;
  lead_quelle: string | null;
  lead_quelle_info: string | null;
  members_count: number;
  /** Namen der betreuten Person(en) (Pflegeempfänger im Konto). */
  care_recipients: string[];
  /** Pflegeparameter-Icon-Keys (CareParamIcons) über alle Pflegeempfänger. */
  care_icons?: string[];
  /**
   * Abwesenheit des Kontos im laufenden Kalendermonat (oder null). Deckt sich
   * mit dem Abwesenheits-Filter: gezählt wird jede Abwesenheit, die sich mit
   * dem Monat überschneidet — auch bereits beendete und bevorstehende.
   * `running` = läuft gerade.
   */
  absence?: FamilyAccountAbsence | null;
  /**
   * Batch-13 Paket C Nacharbeit — Betreuungskraft des primären
   * Pflegeempfängers (bestätigte/laufende Care, status IN (2,3), neuestes
   * startdatum). null = aktuell keine.
   */
  bk_name?: string | null;
  bk_user_id?: number | null;
  /**
   * Ansprechpartner: ein anderes Kontomitglied ohne is_pflegeempfaenger,
   * Fallback die erste `contacts`-Zeile des primären Pflegeempfängers. null =
   * kein Ansprechpartner ermittelbar.
   */
  contact_name?: string | null;
  contact_email?: string | null;
  /** Mind. ein Pflegeempfänger hat SGB V in den Profildaten als benötigt markiert. */
  sgb5_active?: boolean;
  /** Vollständige Mitgliederliste (immer im Detail, in der Liste optional). */
  members: FamilyMember[];
  created_at: string | null;
}

/* ─── Wohnen & Umfeld (jetzt auf Familienkonto-Ebene) ───────────────────── */

export interface WohnenFields {
  wohnsituation?: string | null;
  wohnort?: string | null;
  wohnzustand?: string[];
  haustiere?: string | null;
  haustiere_welche?: string[];
  einkaufsmoeglichkeiten?: string | null;
  /**
   * @deprecated Seit 18.09.2026 nicht mehr auf Kontoebene — Charaktereigenschaften
   * gehören zur Person und werden in den Profildaten (Sektion 04) gepflegt.
   * Die API liefert das Feld nicht mehr aus; die Spalte bleibt für Altdaten bestehen.
   */
  charaktereigenschaften?: string[];
  raucherhaushalt?: string | null;
  /** 23.10.2026: BK Wohnraum (vom PB-Profil ins Familienkonto verschoben). */
  bk_wohnen?: string[];
}

/* ─── Eingabe-Payloads ──────────────────────────────────────────────────── */

/** Body für Konto anlegen/aktualisieren. */
export interface FamilyAccountInput extends WohnenFields {
  name?: string | null;
  assigned_to?: number | null;
  notiz?: string | null;
  lead_quelle?: string | null;
  lead_quelle_info?: string | null;
}

/**
 * Body für „Person hinzufügen" bzw. Profil aktualisieren.
 * `profiltyp` ist Pflicht (Backend 422 sonst). Pflegegrad ist 1..5 oder null
 * (kein Pflegegrad). Alle Stammdaten-/Adressfelder sind optional.
 */
export interface FamilyMemberInput {
  profiltyp: string;
  /** Feedback 11.10.2026 — Mehrfach-Rolle; profiltyp = primäre Rolle. */
  profiltypen?: string[];
  /** Optimistisches Locking: der geladene `updated_at`-Stand (nur bei update). */
  updated_at?: string | null;
  familiengrad?: string[];
  is_pflegeempfaenger?: boolean;
  haushalt?: boolean;
  pflegegrad?: number | null;
  anrede?: string | null;
  titel?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  email?: string | null;
  mobile?: string | null;
  telefon?: string | null;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  ort?: string | null;
  land?: string | null;
  /**
   * Restliche Profil-/User-Felder der Profildaten-Maske (Pflege, Versicherung,
   * Wunsch-BK, Freitexte, Bank, Status …). Sie werden für Pflegeempfänger aus
   * `MEMBER_EXTRA_PROFILE_FIELDS`/`MEMBER_EXTRA_USER_FIELDS` befüllt; der
   * v2-Endpunkt validiert und persistiert sie. Ohne diesen Index-Typ ließ sich
   * hier nur ein 17-Felder-Ausschnitt schicken — der Rest ging verloren.
   */
  [field: string]: unknown;
}

/* ─── List-Filter ───────────────────────────────────────────────────────── */

/**
 * Filter/Sortier-Parameter für GET /v2/admin/family-accounts.
 *
 * Neu gegenüber der initialen Version:
 *   pflegegrad  — Filtert Konten, bei denen MINDESTENS EIN Pflegeempfänger diesen PG hat.
 *   phase       — Filtert auf Konten, bei denen mind. ein Pflegeempfänger diese Phase hat.
 *   konto       — Status-Filterung (konto-Werte 0/1/2/3/4 der betreuten Personen).
 *   bk_name     — Namenssuche auf zugewiesener Betreuungskraft (User-Feld).
 *   absence     — 'active': mind. ein Mitglied ist GENAU JETZT abwesend;
 *                 'month':  mind. ein Mitglied war oder ist im laufenden
 *                           Kalendermonat abwesend (auch bereits beendet oder
 *                           noch bevorstehend);
 *                 'none':   kein Mitglied ist gerade abwesend; leer = kein Filter.
 *   order_by    — Spaltenname für serverseitige Sortierung.
 *   order       — 'asc' | 'desc'
 */
export interface FamilyAccountListParams {
  search?: string;
  /** Zugewiesen an: einzelne Admin-ID oder Mehrfachauswahl (whereIn). */
  assigned_to?: number | number[];
  pflegegrad?: number;
  phase?: number;
  /** konto-Werte der members (z.B. [4] für Aktiv, [2] für Ausstehend, [0,1,3] für Beendet). */
  konto?: number[];
  bk_name?: string;
  /** Abwesenheits-Filter: 'active' (jetzt) | 'month' | 'none' | leer = kein Filter. */
  absence?: 'active' | 'month' | 'none';
  /** Nur Konten, bei denen mind. ein Mitglied die LETZTE Phase erreicht hat. */
  only_final_phase?: boolean;
  /**
   * Batch-13 Paket C Nacharbeit — 'kunden': nur Konten, deren Pflegeempfänger
   * die Kunde-Bedingung erfüllt (letzte aktive Stage + bestätigte Care).
   * 'alle' (oder weggelassen): unverändertes Verhalten. /pflege sendet
   * standardmäßig 'kunden'.
   */
  scope?: 'kunden' | 'alle';
  order_by?: string;
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

function listParams(p: FamilyAccountListParams = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.search) out.search = p.search;
  if (Array.isArray(p.assigned_to)) {
    if (p.assigned_to.length) out['assigned_to[]'] = p.assigned_to;
  } else if (p.assigned_to != null) {
    out.assigned_to = p.assigned_to;
  }
  if (p.pflegegrad != null) out.pflegegrad = p.pflegegrad;
  if (p.phase != null) out.phase = p.phase;
  if (p.konto?.length) out['konto[]'] = p.konto;
  if (p.bk_name) out.bk_name = p.bk_name;
  if (p.absence) out.absence = p.absence;
  if (p.only_final_phase) out.only_final_phase = 1;
  if (p.scope) out.scope = p.scope;
  if (p.order_by) out.order_by = p.order_by;
  if (p.order) out.order = p.order;
  if (p.page != null) out.page = p.page;
  if (p.per_page != null) out.per_page = p.per_page;
  return out;
}

/* ─── Familienkonto-Endpoints ───────────────────────────────────────────── */

export const v2FamilyAccounts = {
  /** GET /v2/admin/family-accounts — paginierte Kontenliste. */
  list: async (params?: FamilyAccountListParams): Promise<V2Paginated<FamilyAccount>> => {
    const res = await apiV2.get<V2Paginated<FamilyAccount>>('/admin/family-accounts', {
      params: listParams(params),
    });
    return { data: res.data.data ?? [], meta: res.data.meta };
  },

  /** GET /v2/admin/family-accounts/{id} — Konto inkl. members[]. */
  get: async (id: number): Promise<FamilyAccount> => {
    const res = await apiV2.get<{ data: FamilyAccount }>(`/admin/family-accounts/${id}`);
    return res.data.data;
  },

  /** POST /v2/admin/family-accounts — neues Konto. */
  create: async (input: FamilyAccountInput): Promise<FamilyAccount> => {
    const res = await apiV2.post<{ data: FamilyAccount }>('/admin/family-accounts', input);
    return res.data.data;
  },

  /** PUT /v2/admin/family-accounts/{id} — Konto-Stammdaten aktualisieren. */
  update: async (id: number, input: FamilyAccountInput): Promise<FamilyAccount> => {
    const res = await apiV2.put<{ data: FamilyAccount }>(`/admin/family-accounts/${id}`, input);
    return res.data.data;
  },

  /**
   * POST /v2/admin/family-accounts/with-member — US-FAM-13 (Waisenkonto-Fix)
   *
   * Legt Konto + erste Person + Leadquelle in EINEM atomaren Request an
   * (Backend: eine DB::transaction()). Ersetzt für CreateAnfrageDrawer die
   * bisherige Kette aus bis zu drei Requests (create Konto → create Person
   * → PATCH Leadquelle), bei der ein gescheiterter zweiter Schritt ein
   * leeres "Waisenkonto" zurückließ.
   *
   * `idempotencyKey`: optionale, clientseitig generierte UUID (z.B.
   * `crypto.randomUUID()`). Bei Wiederholung mit demselben Key innerhalb
   * von 24h liefert das Backend die URSPRÜNGLICHE Antwort erneut (Status
   * 200) statt ein zweites Konto anzulegen — Schutz gegen Doppelklick/
   * Netzwerk-Retry, der über die reine `saving`-Sperre im Formular hinausgeht.
   */
  createWithMember: async (
    input: { account: FamilyAccountInput; member: FamilyMemberInput },
    idempotencyKey?: string,
  ): Promise<{ account: FamilyAccount; member: FamilyMember }> => {
    const res = await apiV2.post<{ data: { account: FamilyAccount; member: FamilyMember } }>(
      '/admin/family-accounts/with-member',
      input,
      idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
    );
    return res.data.data;
  },
};

/* ─── Mitglieder-/Profil-Endpoints ──────────────────────────────────────── */

export const v2Members = {
  /** POST /v2/admin/family-accounts/{accountId}/members — Person hinzufügen. */
  create: async (accountId: number, input: FamilyMemberInput): Promise<FamilyMember> => {
    const res = await apiV2.post<{ data: FamilyMember }>(
      `/admin/family-accounts/${accountId}/members`,
      input,
    );
    return res.data.data;
  },

  /** GET /v2/admin/profiles/{profileId} — einzelne Person laden. */
  get: async (profileId: number): Promise<FamilyMember> => {
    const res = await apiV2.get<{ data: FamilyMember }>(`/admin/profiles/${profileId}`);
    return res.data.data;
  },

  /**
   * PUT /v2/admin/profiles/{profileId} — Person aktualisieren.
   * Enthält `input.updated_at` den geladenen Stand und wurde die Person
   * inzwischen von jemand anderem gespeichert, wirft der Aufruf einen
   * `StaleWriteError` mit `current` = aktueller Stand (409 E_STALE_WRITE).
   */
  update: async (profileId: number, input: FamilyMemberInput): Promise<FamilyMember> => {
    try {
      const res = await apiV2.put<{ data: FamilyMember }>(`/admin/profiles/${profileId}`, input);
      return res.data.data;
    } catch (e) {
      return rethrowStaleWrite<FamilyMember>(e);
    }
  },

  /** DELETE /v2/admin/profiles/{profileId} — Person aus dem Konto entfernen. */
  remove: async (profileId: number): Promise<{ deleted: boolean }> => {
    const res = await apiV2.delete<{ data: { deleted: boolean } }>(`/admin/profiles/${profileId}`);
    return res.data.data;
  },
};

/** PATCH /api/v2/admin/users/{id}/lead-quelle */
export const v2UserLeadQuelle = {
  update: async (userId: number, quelle: string | null, info: string | null): Promise<void> => {
    await apiV2.patch(`/admin/users/${userId}/lead-quelle`, {
      lead_quelle: quelle,
      lead_quelle_info: info,
    });
  },
};

/* ─── Helfer ────────────────────────────────────────────────────────────── */

/**
 * Profiltyp-Werte, die als Pflegeempfänger gelten (Frau/Mann). Wird verwendet,
 * um clientseitig (bevor der Server antwortet) zu entscheiden, ob die vollen
 * Pflege-Sektionen gezeigt werden. Der Server bleibt die Quelle der Wahrheit
 * über `is_care_recipient`.
 */
const CARE_RECIPIENT_LABELS = new Set(['Frau', 'Mann']);

/**
 * Ist der gegebene Profiltyp ein Pflegeempfänger? `opts` = die
 * profiltyp-Optionsliste (id→value), damit auch Options-IDs aufgelöst werden.
 */
export function isCareRecipientType(
  profiltyp: string | null | undefined,
  profiltypOptions?: Array<{ value: string; label: string }>,
): boolean {
  if (!profiltyp) return false;
  // Direkter Label-Treffer (z.B. wenn der Wert das Label selbst ist).
  if (CARE_RECIPIENT_LABELS.has(profiltyp)) return true;
  // Options-ID → Label auflösen.
  const match = profiltypOptions?.find(o => o.value === profiltyp);
  return match ? CARE_RECIPIENT_LABELS.has(match.label) : false;
}

/** Pflegegrad-Auswahl für die Konto-Formulare: „kein" + 1–5. */
export const PFLEGEGRAD_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Kein Pflegegrad' },
  { value: '1', label: 'Pflegegrad 1' },
  { value: '2', label: 'Pflegegrad 2' },
  { value: '3', label: 'Pflegegrad 3' },
  { value: '4', label: 'Pflegegrad 4' },
  { value: '5', label: 'Pflegegrad 5' },
];
